import { Button } from "@frontend/components/ui/button"
import { Checkbox } from "@frontend/components/ui/checkbox"
import { useApiClient, useApiQuery } from "@frontend/hooks/use-api"
import { ChevronRight, File, Folder, Loader2 } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

interface SharedSourceRoot {
  name: string
  path: string
}

interface BrowseEntry {
  name: string
  path: string
  type: "directory" | "file"
  size?: number
}

interface BrowseResult {
  data: BrowseEntry[]
  parent: string | null
}

export interface SelectedSource {
  path: string
  name: string
  format: "ome_tiff" | "ome_zarr"
}

interface SharedSourceBrowserProps {
  onSelectionChange: (sources: SelectedSource[]) => void
  selectedSources: SelectedSource[]
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function inferFormat(name: string): "ome_tiff" | "ome_zarr" {
  if (name.toLowerCase().endsWith(".zarr")) return "ome_zarr"
  return "ome_tiff"
}

export function SharedSourceBrowser({ onSelectionChange, selectedSources }: SharedSourceBrowserProps) {
  const api = useApiClient()
  const [currentPath, setCurrentPath] = useState<string | null>(null)
  const [browseData, setBrowseData] = useState<BrowseResult | null>(null)
  const [browsing, setBrowsing] = useState(false)

  const { data: rootsResult, isLoading: rootsLoading } = useApiQuery<{
    data: SharedSourceRoot[]
  }>(["shared-sources"], "/shared-sources")

  const roots = rootsResult?.data ?? []

  const selectedPaths = useMemo(() => new Set(selectedSources.map((s) => s.path)), [selectedSources])

  const navigateTo = useCallback(
    async (dirPath: string) => {
      setBrowsing(true)
      try {
        const result = await api(`/shared-sources/browse?path=${encodeURIComponent(dirPath)}`)
        setBrowseData(result)
        setCurrentPath(dirPath)
      } finally {
        setBrowsing(false)
      }
    },
    [api]
  )

  const handleToggleFile = useCallback(
    (entry: BrowseEntry) => {
      if (selectedPaths.has(entry.path)) {
        onSelectionChange(selectedSources.filter((s) => s.path !== entry.path))
      } else {
        onSelectionChange([...selectedSources, { path: entry.path, name: entry.name, format: inferFormat(entry.name) }])
      }
    },
    [selectedPaths, selectedSources, onSelectionChange]
  )

  const handleSelectAll = useCallback(() => {
    const allFiles = browseData?.data.filter((e) => e.type === "file") ?? []
    const allSelected = allFiles.length > 0 && allFiles.every((e) => selectedPaths.has(e.path))

    if (allSelected) {
      const currentPaths = new Set(allFiles.map((f) => f.path))
      onSelectionChange(selectedSources.filter((s) => !currentPaths.has(s.path)))
    } else {
      const newSources = allFiles
        .filter((f) => !selectedPaths.has(f.path))
        .map((f) => ({ path: f.path, name: f.name, format: inferFormat(f.name) }))
      onSelectionChange([...selectedSources, ...newSources])
    }
  }, [browseData, selectedPaths, selectedSources, onSelectionChange])

  // Breadcrumb navigation
  const breadcrumbs: { label: string; path: string }[] = []
  if (currentPath) {
    const root = roots.find((r) => currentPath.startsWith(r.path))
    if (root) {
      breadcrumbs.push({ label: root.name, path: root.path })
      const relative = currentPath.slice(root.path.length).replace(/^\//, "")
      if (relative) {
        const parts = relative.split("/")
        let accumulated = root.path
        for (const part of parts) {
          accumulated = `${accumulated}/${part}`
          breadcrumbs.push({ label: part, path: accumulated })
        }
      }
    }
  }

  if (rootsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (roots.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">No shared source directories configured.</div>
  }

  // Root directory listing
  if (!currentPath) {
    return (
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground mb-2">Select a shared source directory to browse:</p>
        {roots.map((root) => (
          <button
            key={root.path}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md hover:bg-accent text-sm text-left"
            onClick={() => navigateTo(root.path)}
          >
            <Folder className="size-4 text-muted-foreground shrink-0" />
            <span className="font-medium">{root.name}</span>
            <span className="text-xs text-muted-foreground truncate ml-auto">{root.path}</span>
          </button>
        ))}
      </div>
    )
  }

  const entries = browseData?.data ?? []
  const files = entries.filter((e) => e.type === "file")
  const allFilesSelected = files.length > 0 && files.every((e) => selectedPaths.has(e.path))

  return (
    <div className="space-y-2">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground overflow-x-auto">
        <button
          className="hover:text-foreground shrink-0"
          onClick={() => {
            setCurrentPath(null)
            setBrowseData(null)
          }}
        >
          Sources
        </button>
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.path} className="flex items-center gap-1">
            <ChevronRight className="size-3 shrink-0" />
            <button
              className={`hover:text-foreground shrink-0 ${
                i === breadcrumbs.length - 1 ? "text-foreground font-medium" : ""
              }`}
              onClick={() => navigateTo(crumb.path)}
            >
              {crumb.label}
            </button>
          </span>
        ))}
      </div>

      {browsing ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Select all / Back */}
          <div className="flex items-center gap-2 pb-1 border-b">
            {browseData?.parent && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigateTo(browseData.parent!)}>
                ← Back
              </Button>
            )}
            {files.length > 0 && (
              <label className="flex items-center gap-1.5 text-xs ml-auto cursor-pointer">
                <Checkbox checked={allFilesSelected} onCheckedChange={() => handleSelectAll()} />
                Select all ({files.length})
              </label>
            )}
          </div>

          {/* Directory contents */}
          <div className="max-h-[480px] overflow-y-auto space-y-0.5">
            {entries.length === 0 && (
              <p className="text-xs text-muted-foreground py-4 text-center">No WSI files found in this directory.</p>
            )}
            {entries.map((entry) => (
              <div key={entry.path} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent text-sm">
                {entry.type === "file" ? (
                  <>
                    <Checkbox checked={selectedPaths.has(entry.path)} onCheckedChange={() => handleToggleFile(entry)} />
                    <File className="size-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{entry.name}</span>
                    {entry.size != null && (
                      <span className="text-xs text-muted-foreground ml-auto tabular-nums shrink-0">
                        {formatBytes(entry.size)}
                      </span>
                    )}
                  </>
                ) : (
                  <button className="flex items-center gap-2 w-full text-left" onClick={() => navigateTo(entry.path)}>
                    <Folder className="size-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{entry.name}</span>
                    <ChevronRight className="size-3 text-muted-foreground ml-auto shrink-0" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
